import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Typography, Link } from "@mui/material";
import { downloadVesselFiles } from "../../services/fleetService";

const progressBarStyle = `
  @keyframes gradientMove {
    0% { background-position: 200% 0; }
    100% { background-position: -100% 0; }
  }
  .animated-gradient {
    animation: gradientMove 5s linear infinite;
    background-size: 300% 100%;
    background-image: linear-gradient(
      90deg,
      #27ade3 0%,
      #27ade3 25%,
      #1a325d 50%,
      #27ade3 75%,
      #27ade3 100%
    );
  }
`;

const LastVoyage = ({ lastVoyage }) => {
  console.log("lastVoyage data: ", lastVoyage);

  const [isOpen, setIsOpen] = useState(true);
  const [openAttachments, setOpenAttachments] = useState(false);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [currentVesselName, setCurrentVesselName] = useState("");

  const handleOpenAttachments = () => {
    setCurrentAttachments(lastVoyage?.attachments || []);
    setCurrentVesselName(lastVoyage?.vessel?.name || "Vessel");
    setOpenAttachments(true);
  };

  const handleCloseAttachments = () => {
    setOpenAttachments(false);
  };

  if (!lastVoyage) {
    return (
      <div className="rounded-lg border border-gray-200 p-4 shadow-sm">
        Loading voyage data...
      </div>
    );
  }

  // Map API data to component structure
  const fuelEmissionsMetrics = [
    {
      label: "Hours Underway",
      value: lastVoyage?.timeTravelled != null
        ? `${lastVoyage.timeTravelled} Hours`
        : "N/A",
    },
    {
      label: "Total Distance",
      value: lastVoyage.totalDistance
        ? `${lastVoyage.totalDistance} NM`
        : "N/A",
    },
    {
      label: "Distance Sailed",
      value: lastVoyage.distanceTravelled
        ? `${lastVoyage.distanceTravelled} NM`
        : "N/A",
    },
    {
      label: "Fuel Consumption Avg (MT/day)",
      value: lastVoyage.consumptionAvg?.toString() || "N/A",
    },
    {
      label: "Speed Avg (kn)",
      value: lastVoyage.avgSpeed?.toString() || "N/A",
    },
  ];

  const totalFuelConsumption = [
    {
      label: "Heavy Fuel Oil",
      value: lastVoyage.hfoConsumption ? `${lastVoyage.hfoConsumption} MT` : "N/A",
    },
    {
      label: "Light Fuel Oil",
      value: lastVoyage.lfoConsumption ? `${lastVoyage.lfoConsumption} MT` : "N/A",
    },
    {
      label: "Marine Gas Oil",
      value: lastVoyage.mgoConsumption ? `${lastVoyage.mgoConsumption} MT` : "N/A",
    },
    {
      label: "Others",
      value: lastVoyage.othersConsumption ? `${lastVoyage.othersConsumption} MT` : "N/A",
    },
  ];

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getProgressPercentage = () => {
    if (lastVoyage.percentageTravelled !== undefined) {
      return Math.min(100, Math.max(0, lastVoyage.percentageTravelled));
    }
  };

  const FALLBACK_FLAG = "https://flagcdn.com/w160/us.png";

  const isValidFlagUrl = (url) =>
    typeof url === "string" &&
    url.startsWith("http");

  const getFromPortCountryFlag = () => {
    return isValidFlagUrl(lastVoyage?.fromPortFlag)
      ? lastVoyage.fromPortFlag
      : FALLBACK_FLAG;
  };

  const getToPortCountryFlag = () => {
    return isValidFlagUrl(lastVoyage?.toPortFlag)
      ? lastVoyage.toPortFlag
      : FALLBACK_FLAG;
  };

  const handleDownload = async (file) => {
    const response = await downloadVesselFiles(file);
  };

  const progressPercentage = getProgressPercentage();

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <style>{progressBarStyle}</style>
      {openAttachments && (
        <div className="relative z-50">
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded bg-white p-6 shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0a2540]">
                  Attachments for IMO{" "}
                  <span className="text-[#27ade3]">{currentVesselName}</span>
                </h3>
              </div>

              {/* Attachments List */}
              <div className="space-y-2">
                {currentAttachments.length > 0 ? (
                  <div className="space-y-3">
                    {currentAttachments.map((file, index) => (
                      <a
                        key={index}
                        // href={file.url}
                        onClick={() => handleDownload(file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                            {file.type === "pdf" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                            ) : file.type === "docx" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            ) : file.type === "xlsx" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800 truncate max-w-[180px]">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.type.toUpperCase()} file • {file.date}
                            </p>
                          </div>
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-400 hover:text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-gray-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      No attachments available
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseAttachments}
                  className="px-4 py-2 bg-[#0a2540] text-white rounded hover:bg-[#27ade3] text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className="flex justify-between items-center px-4 py-4 cursor-pointer bg-gradient-to-r from-[#1a325d] to-[#27ade3]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-white font-normal text-lg">
          Last <span className="font-semibold">Voyage</span>
        </h3>
        <button className="p-1 rounded-full">
          {isOpen ? (
            <ChevronUp color="#fff" size={20} />
          ) : (
            <ChevronDown color="#fff" size={20} />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="px-4 py-6 bg-gradient-to-tr from-white to-blue-50 border-t border-blue-200">
          <div className="pb-4 w-full">
            {/* Ports Section */}
            <div className="flex justify-between items-center gap-3 flex-grow">
              {/* Departure */}
              <div className="flex items-center gap-3">
                <img
                  src={`${getFromPortCountryFlag()}`}
                  alt={`${lastVoyage.fromPort} flag`}
                  className="w-24 h-16 object-cover border border-gray-200 rounded-lg flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-700 truncate">
                    {lastVoyage.fromPort || "N/A"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Depart:{" "}
                    <span className="text-blue-600 font-semibold">
                      {formatDate(lastVoyage.departTime)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex text-gray-400">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </div>

              {/* Arrival */}
              <div className="flex items-center gap-3">
                <img
                  src={`${getToPortCountryFlag()}`}
                  alt={`${lastVoyage.toPort} flag`}
                  className="w-24 h-16 object-cover border border-gray-200 rounded-lg flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-700 truncate">
                    {lastVoyage.toPort || "N/A"}
                  </div>
                </div>
              </div>

              {/* Attachments */}
              <div className="px-8 border-l border-gray-300">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Attachments
                  </div>
                  <button
                    onClick={() => handleOpenAttachments()}
                    className="px-4 py-2 text-sm font-bold rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-200 cursor-pointer min-w-[50px]"
                  >
                    {lastVoyage.attachments?.length || 0}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Fuel & Emissions */}
          <div className="border-t border-gray-300 py-4">
            <div className="flex flex-wrap gap-4 justify-between">
              {fuelEmissionsMetrics.map((item, index) => (
                <div key={index}>
                  <div>
                    <span className="text-[14px] text-gray-500">
                      {item.label}
                    </span>
                    <p className="text-sm font-semibold text-[#27ade3]">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Fuel Consumption */}
          <div className="border-t border-gray-300 py-4">
            <div className="flex flex-wrap gap-4 justify-between">
              {totalFuelConsumption.map((item, index) => (
                <div key={index}>
                  <div>
                    <span className="text-[14px] text-gray-500">
                      {item.label}
                    </span>
                    <p className="text-sm font-semibold text-[#27ade3]">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LastVoyage;
